import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded"
import CancelRoundedIcon from "@mui/icons-material/CancelRounded"

import MainLayout from "../../layouts/MainLayout"
import {
  createDeposit,
  deleteDeposit,
  getDepositById,
  getDepositFormData,
  getDeposits,
  updateDeposit,
} from "../../services/depositService"

const CENTRAL_CONFIRM_ROLES = [
  "platform_admin",
  "super_admin",
  "dg",
  "gestionnaire_operations",
  "caissier_central",
]

const MANAGE_DEPOSIT_ROLES = [
  ...CENTRAL_CONFIRM_ROLES,

  "admin_maison_djo",
  "gestionnaire_maison_djo",
  "controleur_maison_djo",
  "caissier_maison_djo",
  "recouvreur_maison_djo",

  "admin_depot_relais",
  "gestionnaire_depot_relais",
  "controleur_depot_relais",
  "caissier_depot_relais",
  "recouvreur_depot_relais",
]

const STORE_LOCKED_ROLES = ["gerant_magasin", "gerant_depot_relais"]

const ROWS_PER_PAGE = 10

const defaultFormData = {
  company_id: 1,
  store_id: "",
  user_id: "",
  deposit_date: "",
  amount: "",
  deposit_currency: "USD",
  exchange_rate: "",
  amount_usd: "",
  amount_cdf: "",
  deposit_method: "cash",
  bank_reference: "",
  status: "pending",
  notes: "",
}

function normalizeDecimalInput(value) {
  const raw = String(value ?? "").trim().replace(/\s/g, "")

  if (!raw) return ""

  const lastComma = raw.lastIndexOf(",")
  const lastDot = raw.lastIndexOf(".")

  if (lastComma > -1 && lastDot > -1) {
    const decimalSeparator = lastComma > lastDot ? "," : "."
    const thousandSeparator = decimalSeparator === "," ? "." : ","

    return raw
      .replace(new RegExp(`\\${thousandSeparator}`, "g"), "")
      .replace(decimalSeparator, ".")
  }

  return raw.replace(",", ".")
}

function parseDecimalInput(value, fallback = 0) {
  const normalized = normalizeDecimalInput(value)

  if (normalized === "") return fallback

  const parsed = Number(normalized)

  return Number.isFinite(parsed) ? parsed : fallback
}

function handleMoneyInputValue(value) {
  return String(value ?? "").replace(/[^\d.,\s]/g, "")
}

function toNumber(value, fallback = 0) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : fallback
  }

  return parseDecimalInput(value, fallback)
}

function getLocalValue(key, fallback = "") {
  const value = localStorage.getItem(key)
  return value && value !== "undefined" && value !== "null" ? value : fallback
}

function getLocalNumber(key, fallback = "") {
  const value = getLocalValue(key, "")
  if (value === "") return fallback

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function formatNumber(value) {
  return toNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatUsd(value) {
  return `${formatNumber(value)} USD`
}

function formatCdf(value) {
  return `${toNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} CDF`
}

function formatDate(value) {
  if (!value) return "-"

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"

  return date.toLocaleDateString("fr-FR")
}

function formatDateTimeForInput(value) {
  if (!value) return ""

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""

  const offset = date.getTimezoneOffset()
  const localDate = new Date(date.getTime() - offset * 60000)

  return localDate.toISOString().slice(0, 16)
}

function getNowForInput() {
  const date = new Date()
  const offset = date.getTimezoneOffset()
  const localDate = new Date(date.getTime() - offset * 60000)

  return localDate.toISOString().slice(0, 16)
}

function methodLabel(value) {
  const labels = {
    cash: "Espèces",
    bank_transfer: "Virement bancaire",
    mobile_money: "Mobile Money",
    cheque: "Chèque",
  }

  return labels[value] || value || "-"
}

function statusLabel(value) {
  const labels = {
    confirmed: "Confirmé caisse centrale",
    pending: "En attente caisse centrale",
    cancelled: "Annulé",
  }

  return labels[value] || value || "-"
}

function getStatusColor(value) {
  if (value === "confirmed") return "success"
  if (value === "pending") return "warning"
  if (value === "cancelled") return "error"

  return "default"
}

function getDepositRowClass(status) {
  if (status === "confirmed") {
    return "deposit-row deposit-row--confirmed"
  }

  if (status === "pending") {
    return "deposit-row deposit-row--pending"
  }

  if (status === "cancelled") {
    return "deposit-row deposit-row--cancelled"
  }

  return "deposit-row deposit-row--default"
}

function roleCanConfirmCentral(role) {
  return CENTRAL_CONFIRM_ROLES.includes(role)
}

function computeMoney(formData) {
  const currency = formData.deposit_currency || "USD"
  const amount = parseDecimalInput(formData.amount)
  const exchangeRate = parseDecimalInput(formData.exchange_rate)

  if (currency === "CDF") {
    const amountCdf = amount
    const amountUsd = exchangeRate > 0 ? amountCdf / exchangeRate : 0

    return {
      amount_usd: amountUsd,
      amount_cdf: amountCdf,
    }
  }

  const amountUsd = amount
  const amountCdf = exchangeRate > 0 ? amountUsd * exchangeRate : 0

  return {
    amount_usd: amountUsd,
    amount_cdf: amountCdf,
  }
}

function getOriginalAmount(row) {
  const currency = row.deposit_currency || "USD"

  if (currency === "CDF") {
    return toNumber(row.amount_cdf)
  }

  return toNumber(row.amount_usd ?? row.amount)
}

function MetricCard({
  label,
  value,
  secondValue,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: { xs: 1.35, sm: 1.5 },
        py: 1.15,
        minWidth: 0,
        borderRadius: 3,
        borderColor: "rgba(148, 163, 184, 0.22)",
        bgcolor: "#ffffff",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.1,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            borderRadius: 2.5,
            color: accent,
            bgcolor: background,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 0.7,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 18, sm: 20 },
                lineHeight: 1,
                fontWeight: 900,
                color: "#0f172a",
                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                minWidth: 0,
                fontWeight: 800,
                color: "#334155",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>

          {secondValue ? (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.3,
                color: "#64748b",
                fontWeight: 800,
                lineHeight: 1.2,
              }}
            >
              {secondValue}
            </Typography>
          ) : null}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: secondValue ? 0.1 : 0.35,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


function MobileDepositCard({
  deposit,
  canManageDeposits,
  canDeleteDeposits,
  canConfirmCentralDeposit,
  onEdit,
  onDelete,
}) {
  const currency = deposit.deposit_currency || "USD"
  const originalAmount = getOriginalAmount(deposit)

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "rgba(148, 163, 184, 0.22)",
        boxShadow:
          deposit.status === "confirmed"
            ? "inset 4px 0 0 #22c55e, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : deposit.status === "pending"
            ? "inset 4px 0 0 #f59e0b, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : deposit.status === "cancelled"
            ? "inset 4px 0 0 #ef4444, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : "inset 4px 0 0 #cbd5e1, 0 5px 16px rgba(15, 23, 42, 0.025)",
      }}
    >
      <CardContent
        sx={{
          p: 1.5,
          "&:last-child": { pb: 1.5 },
        }}
      >
        <Stack spacing={1.2}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={900} color="#0f172a">
                {deposit.deposit_code || "-"}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {formatDate(deposit.deposit_date)}
              </Typography>
            </Box>

            <Chip
              label={statusLabel(deposit.status)}
              color={getStatusColor(deposit.status)}
              size="small"
              sx={{
                maxWidth: 165,
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 1,
              borderRadius: 2.5,
              bgcolor: "#fbfdff",
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              Origine
            </Typography>

            <Typography variant="body2" fontWeight={800} color="#334155">
              {deposit.store_name || "-"}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {[deposit.store_code, deposit.organization_name, deposit.user_name]
                .filter(Boolean)
                .join(" • ")}
            </Typography>
          </Paper>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 0.8,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 0.9,
                borderRadius: 2.5,
                bgcolor: "#f8fafc",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Montant remis
              </Typography>

              <Typography fontWeight={900} color="#0f172a">
                {currency === "CDF"
                  ? formatCdf(originalAmount)
                  : formatUsd(originalAmount)}
              </Typography>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 0.9,
                borderRadius: 2.5,
                bgcolor: "#f8fafc",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Équiv. USD
              </Typography>

              <Typography fontWeight={900} color="#0f172a">
                {formatUsd(deposit.amount_usd ?? deposit.amount)}
              </Typography>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 0.9,
                borderRadius: 2.5,
                bgcolor: "#f8fafc",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Méthode
              </Typography>

              <Typography variant="body2" fontWeight={800}>
                {methodLabel(deposit.deposit_method)}
              </Typography>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 0.9,
                borderRadius: 2.5,
                bgcolor:
                  toNumber(deposit.shop_debt_remaining_amount) > 0
                    ? "rgba(220, 38, 38, 0.05)"
                    : "rgba(22, 163, 74, 0.05)",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Dette shop
              </Typography>

              <Typography
                fontWeight={900}
                color={
                  toNumber(deposit.shop_debt_remaining_amount) > 0
                    ? "error.main"
                    : "success.main"
                }
              >
                {formatUsd(deposit.shop_debt_remaining_amount)}
              </Typography>
            </Paper>
          </Box>

          {deposit.bank_reference ? (
            <Typography variant="caption" color="text.secondary">
              Référence : {deposit.bank_reference}
            </Typography>
          ) : null}

          {canManageDeposits ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: canDeleteDeposits
                  ? "repeat(2, minmax(0, 1fr))"
                  : "1fr",
                gap: 0.7,
              }}
            >
              <Button
                size="small"
                variant="contained"
                disableElevation
                onClick={() => onEdit(deposit.id)}
              >
                {deposit.status === "pending" && canConfirmCentralDeposit
                  ? "Confirmer"
                  : "Modifier"}
              </Button>

              {canDeleteDeposits && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => onDelete(deposit.id, deposit.status)}
                >
                  Supprimer
                </Button>
              )}
            </Box>
          ) : (
            <Chip label="Lecture seule" size="small" variant="outlined" />
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}


function DepositsPage() {
  const theme = useTheme()
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"))
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"))

  const currentUserId = getLocalNumber("kongosys_user_id", 1)
  const currentRole = getLocalValue("kongosys_role", "viewer")
  const currentStoreId = getLocalNumber("kongosys_store_id", "")
  const currentStoreName = getLocalValue("kongosys_store_name", "")
  const currentOrganizationName = getLocalValue("kongosys_organization_name", "")

  const [permissions, setPermissions] = useState({
    can_create: MANAGE_DEPOSIT_ROLES.includes(currentRole),
    can_update: MANAGE_DEPOSIT_ROLES.includes(currentRole),
    can_delete: MANAGE_DEPOSIT_ROLES.includes(currentRole),
    can_confirm_central: roleCanConfirmCentral(currentRole),
    role: currentRole,
  })

  const canManageDeposits = Boolean(permissions.can_create || permissions.can_update)
  const canDeleteDeposits = Boolean(permissions.can_delete)
  const canConfirmCentralDeposit = Boolean(permissions.can_confirm_central)

  const isStoreLocked =
    STORE_LOCKED_ROLES.includes(currentRole) && currentStoreId !== ""

  const [deposits, setDeposits] = useState([])
  const [stores, setStores] = useState([])
  const [users, setUsers] = useState([])
  const [depositMethods, setDepositMethods] = useState([])
  const [statuses, setStatuses] = useState([])

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const [open, setOpen] = useState(false)
  const [selectedDeposit, setSelectedDeposit] = useState(null)

  const [formData, setFormData] = useState({
    ...defaultFormData,
    deposit_date: getNowForInput(),
    store_id: isStoreLocked ? Number(currentStoreId) : "",
    user_id: currentUserId || "",
    status: roleCanConfirmCentral(currentRole) ? "confirmed" : "pending",
  })

  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [methodFilter, setMethodFilter] = useState("")

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: ROWS_PER_PAGE,
  })

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const buildDefaultFormData = () => ({
    ...defaultFormData,
    deposit_date: getNowForInput(),
    store_id: isStoreLocked ? Number(currentStoreId) : "",
    user_id: currentUserId || "",
    status: canConfirmCentralDeposit ? "confirmed" : "pending",
  })

  const loadDeposits = async (customFilters = null) => {
    try {
      setLoading(true)

      const filters =
        customFilters || {
          search,
          status: statusFilter,
          deposit_method: methodFilter,
        }

      const data = await getDeposits(filters)

      setDeposits(Array.isArray(data) ? data : [])

      setPaginationModel({
        page: 0,
        pageSize: ROWS_PER_PAGE,
      })
    } catch (error) {
      console.error("Error loading deposits:", error)

      showSnackbar(
        error?.response?.data?.detail ||
          error?.message ||
          "Impossible de charger les dépôts caisse centrale.",
        "error"
      )
    } finally {
      setLoading(false)
    }
  }

  const loadFormData = async () => {
    try {
      const data = await getDepositFormData()

      setStores(Array.isArray(data?.stores) ? data.stores : [])
      setUsers(Array.isArray(data?.users) ? data.users : [])
      setDepositMethods(
        Array.isArray(data?.deposit_methods) ? data.deposit_methods : []
      )
      setStatuses(Array.isArray(data?.statuses) ? data.statuses : [])

      if (data?.permissions) {
        setPermissions((prev) => ({
          ...prev,
          ...data.permissions,
        }))
      }
    } catch (error) {
      console.error("Error loading deposit form data:", error)

      showSnackbar(
        error?.response?.data?.detail ||
          error?.message ||
          "Impossible de charger les données du formulaire.",
        "error"
      )
    }
  }

  useEffect(() => {
    loadDeposits()
    loadFormData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetForm = () => {
    setSelectedDeposit(null)
    setFormData(buildDefaultFormData())
  }

  const openAddDialog = () => {
    if (!permissions.can_create) {
      showSnackbar(
        "Ce rôle peut consulter les dépôts, mais ne peut pas déclarer un versement.",
        "warning"
      )
      return
    }

    resetForm()
    setOpen(true)
  }

  const openEditDialog = async (depositId) => {
    if (!permissions.can_update) {
      showSnackbar("Modification non autorisée pour ce rôle.", "warning")
      return
    }

    try {
      const deposit = await getDepositById(depositId)

      if (deposit.status === "confirmed" && !canConfirmCentralDeposit) {
        showSnackbar(
          "Ce dépôt est déjà confirmé par la caisse centrale. Vous pouvez le consulter, mais pas le modifier.",
          "warning"
        )
        return
      }

      const currency = deposit.deposit_currency || "USD"
      const originalAmount =
        currency === "CDF"
          ? toNumber(deposit.amount_cdf)
          : toNumber(deposit.amount_usd ?? deposit.amount)

      setSelectedDeposit(deposit)
      setFormData({
        company_id: deposit.company_id ?? 1,
        store_id: deposit.store_id ?? "",
        user_id: deposit.user_id ?? "",
        deposit_date: formatDateTimeForInput(deposit.deposit_date),
        amount: originalAmount ? String(originalAmount) : "",
        deposit_currency: currency,
        exchange_rate:
          deposit.exchange_rate !== null && deposit.exchange_rate !== undefined
            ? String(deposit.exchange_rate)
            : "",
        amount_usd: deposit.amount_usd ?? "",
        amount_cdf: deposit.amount_cdf ?? "",
        deposit_method: deposit.deposit_method || "cash",
        bank_reference: deposit.bank_reference || "",
        status: canConfirmCentralDeposit ? deposit.status || "confirmed" : "pending",
        notes: deposit.notes || "",
      })

      setOpen(true)
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          error?.message ||
          "Impossible de charger le dépôt.",
        "error"
      )
    }
  }

  const handleClose = () => {
    if (saving) return
    setOpen(false)
    resetForm()
  }

  const handleChange = (event) => {
    const { name, value } = event.target

    setFormData((prev) => ({
      ...prev,
      [name]: ["company_id", "store_id", "user_id"].includes(name)
        ? value === ""
          ? ""
          : Number(value)
        : ["amount", "exchange_rate"].includes(name)
        ? handleMoneyInputValue(value)
        : value,
    }))
  }

  const selectedStore = useMemo(() => {
    return stores.find((item) => Number(item.id) === Number(formData.store_id))
  }, [stores, formData.store_id])

  const selectedStoreLabel = useMemo(() => {
    if (isStoreLocked) {
      return currentStoreName || "Shop assigné"
    }

    if (!selectedStore) return ""

    return selectedStore.code
      ? `${selectedStore.code} - ${selectedStore.name}`
      : selectedStore.name
  }, [isStoreLocked, currentStoreName, selectedStore])

  const formMoney = useMemo(() => computeMoney(formData), [formData])

  const expectedToDeposit = toNumber(selectedStore?.expected_to_deposit)
  const paidSalesAmount = toNumber(selectedStore?.paid_sales_amount)
  const confirmedExpensesAmount = toNumber(selectedStore?.confirmed_expenses_amount)
  const confirmedDepositsAmount = toNumber(selectedStore?.confirmed_deposits_amount)
  const shopDebtRemainingAmount = toNumber(selectedStore?.shop_debt_remaining_amount)

  const validateForm = () => {
    const amountValue = parseDecimalInput(formData.amount)
    const exchangeRateValue = parseDecimalInput(formData.exchange_rate)

    if (!formData.store_id) {
      showSnackbar("Le shop / point d'origine de l'argent est obligatoire.", "warning")
      return false
    }

    if (formData.amount === "" || amountValue <= 0) {
      showSnackbar("Le montant déposé doit être supérieur à zéro.", "warning")
      return false
    }

    if (formData.deposit_currency === "CDF" && exchangeRateValue <= 0) {
      showSnackbar(
        "Le taux de change est obligatoire quand le montant est saisi en CDF.",
        "warning"
      )
      return false
    }

    if (!formData.deposit_method) {
      showSnackbar("La méthode de dépôt est obligatoire.", "warning")
      return false
    }

    if (formMoney.amount_usd > expectedToDeposit && expectedToDeposit > 0) {
      const confirmed = window.confirm(
        `Le montant saisi équivaut à ${formatUsd(
          formMoney.amount_usd
        )}. Il dépasse le reste caisse théorique à déposer pour ce shop (${formatUsd(
          expectedToDeposit
        )}). Voulez-vous continuer ?`
      )

      if (!confirmed) return false
    }

    return true
  }

  const handleSave = async () => {
    const isUpdate = Boolean(selectedDeposit?.id)

    if ((isUpdate && !permissions.can_update) || (!isUpdate && !permissions.can_create)) {
      showSnackbar("Action non autorisée.", "warning")
      return
    }

    if (!validateForm()) return

    const forcedStatus = canConfirmCentralDeposit
      ? formData.status || "confirmed"
      : "pending"

    const amountValue = parseDecimalInput(formData.amount)
    const exchangeRateValue = parseDecimalInput(formData.exchange_rate)

    const payload = {
      company_id: Number(formData.company_id || 1),
      store_id: Number(formData.store_id),
      user_id: formData.user_id ? Number(formData.user_id) : null,
      deposit_date: formData.deposit_date || null,
      amount: amountValue,
      deposit_currency: formData.deposit_currency || "USD",
      exchange_rate: formData.exchange_rate ? exchangeRateValue : null,
      amount_usd: Number(formMoney.amount_usd || 0),
      amount_cdf: formMoney.amount_cdf > 0 ? Number(formMoney.amount_cdf) : null,
      deposit_method: formData.deposit_method,
      bank_reference: formData.bank_reference?.trim() || null,
      status: forcedStatus,
      notes: formData.notes?.trim() || null,
      created_by: Number(currentUserId || 1),
    }

    try {
      setSaving(true)

      if (selectedDeposit?.id) {
        await updateDeposit(selectedDeposit.id, payload)
        showSnackbar(
          forcedStatus === "confirmed"
            ? "Dépôt confirmé par la caisse centrale."
            : "Versement modifié et gardé en attente caisse centrale."
        )
      } else {
        await createDeposit(payload)
        showSnackbar(
          forcedStatus === "confirmed"
            ? "Dépôt confirmé par la caisse centrale."
            : "Versement déclaré. Il attend la confirmation de la caisse centrale."
        )
      }

      setOpen(false)
      resetForm()

      await loadDeposits()
      await loadFormData()
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          error?.message ||
          "Impossible d’enregistrer le dépôt.",
        "error"
      )
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (depositId, status) => {
    if (!canDeleteDeposits) {
      showSnackbar("Suppression non autorisée pour ce rôle.", "warning")
      return
    }

    if (status === "confirmed" && !canConfirmCentralDeposit) {
      showSnackbar(
        "Seule la caisse centrale peut supprimer un dépôt déjà confirmé.",
        "warning"
      )
      return
    }

    const confirmed = window.confirm(
      "Voulez-vous vraiment supprimer ce dépôt caisse centrale ?"
    )

    if (!confirmed) return

    try {
      await deleteDeposit(depositId)
      showSnackbar("Dépôt caisse supprimé avec succès.")
      await loadDeposits()
      await loadFormData()
    } catch (error) {
      console.error(error)

      showSnackbar(
        error?.response?.data?.detail ||
          error?.message ||
          "Impossible de supprimer le dépôt.",
        "error"
      )
    }
  }

  const filteredDeposits = useMemo(() => {
    const keyword = search.trim().toLowerCase()

    return deposits.filter((item) => {
      const matchesSearch =
        keyword === "" ||
        String(item.deposit_code || "").toLowerCase().includes(keyword) ||
        String(item.store_name || "").toLowerCase().includes(keyword) ||
        String(item.store_code || "").toLowerCase().includes(keyword) ||
        String(item.organization_name || "").toLowerCase().includes(keyword) ||
        String(item.user_name || "").toLowerCase().includes(keyword) ||
        String(item.created_by_name || "").toLowerCase().includes(keyword) ||
        String(item.deposit_method || "").toLowerCase().includes(keyword) ||
        String(item.deposit_currency || "").toLowerCase().includes(keyword) ||
        String(item.bank_reference || "").toLowerCase().includes(keyword) ||
        String(item.notes || "").toLowerCase().includes(keyword)

      const matchesStatus =
        statusFilter === "" || String(item.status || "") === statusFilter

      const matchesMethod =
        methodFilter === "" || String(item.deposit_method || "") === methodFilter

      return matchesSearch && matchesStatus && matchesMethod
    })
  }, [deposits, search, statusFilter, methodFilter])

  useEffect(() => {
    setPaginationModel((previous) => ({
      ...previous,
      page: 0,
      pageSize: ROWS_PER_PAGE,
    }))
  }, [search, statusFilter, methodFilter])

  const paginatedMobileDeposits = useMemo(() => {
    const start = paginationModel.page * ROWS_PER_PAGE

    return filteredDeposits.slice(
      start,
      start + ROWS_PER_PAGE
    )
  }, [filteredDeposits, paginationModel.page])

  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(filteredDeposits.length / ROWS_PER_PAGE)
    )

    if (paginationModel.page >= totalPages) {
      setPaginationModel((previous) => ({
        ...previous,
        page: totalPages - 1,
        pageSize: ROWS_PER_PAGE,
      }))
    }
  }, [filteredDeposits.length, paginationModel.page])


  const summary = useMemo(() => {
    const confirmed = filteredDeposits.filter(
      (item) => String(item.status || "") === "confirmed"
    )

    const pending = filteredDeposits.filter(
      (item) => String(item.status || "") === "pending"
    )

    const cancelled = filteredDeposits.filter(
      (item) => String(item.status || "") === "cancelled"
    )

    return {
      count: filteredDeposits.length,
      confirmedUsd: confirmed.reduce(
        (total, item) => total + toNumber(item.amount_usd ?? item.amount),
        0
      ),
      confirmedCdf: confirmed.reduce(
        (total, item) => total + toNumber(item.amount_cdf),
        0
      ),
      pendingUsd: pending.reduce(
        (total, item) => total + toNumber(item.amount_usd ?? item.amount),
        0
      ),
      pendingCdf: pending.reduce(
        (total, item) => total + toNumber(item.amount_cdf),
        0
      ),
      cancelledUsd: cancelled.reduce(
        (total, item) => total + toNumber(item.amount_usd ?? item.amount),
        0
      ),
      cancelledCdf: cancelled.reduce(
        (total, item) => total + toNumber(item.amount_cdf),
        0
      ),
    }
  }, [filteredDeposits])

  const statusOptions = useMemo(() => {
    const baseStatuses =
      statuses.length > 0
        ? statuses
        : [
            { value: "pending", label: "En attente caisse centrale" },
            { value: "confirmed", label: "Confirmé caisse centrale" },
            { value: "cancelled", label: "Annulé" },
          ]

    if (canConfirmCentralDeposit) return baseStatuses

    return baseStatuses.filter((item) => item.value === "pending")
  }, [statuses, canConfirmCentralDeposit])

  const columns = useMemo(
    () => [
      {
        field: "deposit",
        headerName: "Dépôt",
        minWidth: 180,
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Stack
            spacing={0.16}
            justifyContent="center"
            sx={{
              width: "100%",
              height: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: "0.86rem",
                fontWeight: 900,
                lineHeight: 1.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.deposit_code || "-"}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                fontWeight: 700,
                lineHeight: 1.15,
              }}
            >
              {formatDate(params.row.deposit_date)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "origin",
        headerName: "Origine",
        minWidth: 230,
        flex: 1.25,
        sortable: false,
        renderCell: (params) => (
          <Stack
            spacing={0.14}
            justifyContent="center"
            sx={{
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                color: "#334155",
                fontSize: "0.83rem",
                fontWeight: 900,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.store_name || "-"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
            >
              {[
                params.row.organization_name,
                params.row.user_name,
              ]
                .filter(Boolean)
                .join(" • ") || "Origine non renseignée"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "amounts",
        headerName: "Montant",
        minWidth: 210,
        flex: 1.1,
        sortable: false,
        renderCell: (params) => {
          const currency = params.row.deposit_currency || "USD"
          const originalAmount = getOriginalAmount(params.row)

          return (
            <Stack spacing={0.12} sx={{ width: "100%" }}>
              <Typography
                sx={{
                  color: "#0f172a",
                  fontSize: "0.85rem",
                  fontWeight: 900,
                  lineHeight: 1.2,
                }}
              >
                {currency === "CDF"
                  ? formatCdf(originalAmount)
                  : formatUsd(originalAmount)}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: "#475569",
                  fontWeight: 800,
                  lineHeight: 1.15,
                }}
              >
                Équiv. USD : {formatUsd(params.row.amount_usd ?? params.row.amount)}
              </Typography>

              {params.row.amount_cdf ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748b",
                    fontWeight: 700,
                    lineHeight: 1.15,
                  }}
                >
                  Équiv. CDF : {formatCdf(params.row.amount_cdf)}
                </Typography>
              ) : null}
            </Stack>
          )
        },
      },
      {
        field: "deposit_method",
        headerName: "Méthode",
        width: 145,
        renderCell: (params) => (
          <Chip
            label={methodLabel(params.value)}
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              maxWidth: 130,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        ),
      },
      {
        field: "shop_debt_remaining_amount",
        headerName: "Dette shop",
        width: 135,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography
            sx={{
              width: "100%",
              textAlign: "right",
              fontSize: "0.84rem",
              fontWeight: 900,
              color:
                toNumber(params.value) > 0
                  ? "#dc2626"
                  : "#15803d",
            }}
          >
            {formatUsd(params.value)}
          </Typography>
        ),
      },
      {
        field: "status",
        headerName: "Statut caisse",
        width: 175,
        renderCell: (params) => (
          <Chip
            label={statusLabel(params.value)}
            size="small"
            color={getStatusColor(params.value)}
            sx={{
              maxWidth: 165,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
            }}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: canManageDeposits ? 205 : 125,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) =>
          canManageDeposits ? (
            <Stack direction="row" spacing={0.55}>
              <Button
                size="small"
                variant="contained"
                disableElevation
                onClick={() => openEditDialog(params.row.id)}
                sx={{ whiteSpace: "nowrap" }}
              >
                {params.row.status === "pending" && canConfirmCentralDeposit
                  ? "Confirmer"
                  : "Modifier"}
              </Button>

              {canDeleteDeposits && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => handleDelete(params.row.id, params.row.status)}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Supprimer
                </Button>
              )}
            </Stack>
          ) : (
            <Chip label="Lecture seule" size="small" variant="outlined" />
          ),
      },
    ],
    [canManageDeposits, canDeleteDeposits, canConfirmCentralDeposit]
  )

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          pb: { xs: 2, md: 4 },

          "& .MuiButton-root": {
            minHeight: 40,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 800,
            boxShadow: "none",
          },

          "& .MuiOutlinedInput-root": {
            borderRadius: 2.5,
            bgcolor: "#ffffff",
          },

          "& .MuiChip-root": {
            fontWeight: 800,
          },

          "& .MuiPaper-root": {
            backgroundImage: "none",
          },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "minmax(0, 1fr) auto",
            },
            gap: 1.5,
            alignItems: "start",
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: "#0f172a",
                lineHeight: 1.12,
                letterSpacing: "-0.025em",
                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Dépôts caisse centrale
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.4,
                lineHeight: 1.5,
                maxWidth: 780,
              }}
            >
              Versements réellement remis par les shops, recouvreurs ou bureaux
              vers la caisse centrale.
            </Typography>

            {currentOrganizationName && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Organisation : {currentOrganizationName}
              </Typography>
            )}
          </Box>

          {permissions.can_create && (
            <Button
              variant="contained"
              disableElevation
              onClick={openAddDialog}
              sx={{
                minWidth: { xs: "100%", sm: 205 },
                justifySelf: { xs: "stretch", sm: "end" },
              }}
            >
              + Déclarer un versement
            </Button>
          )}
        </Box>

        <Alert
          severity="info"
          variant="outlined"
          sx={{
            mb: 1.6,
            borderRadius: 3,
            py: 0.35,
          }}
        >
          Cette page confirme l’argent réellement remis à la caisse centrale. Les
          dépenses confirmées du shop sont déduites du reste caisse théorique. La
          dette restante du shop est affichée pour contrôle, mais son règlement
          officiel se fait dans la page <strong>Règlement des dettes</strong>.
        </Alert>

        {!canConfirmCentralDeposit && permissions.can_create && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{
              mb: 1.6,
              borderRadius: 3,
              py: 0.35,
            }}
          >
            Votre rôle peut déclarer le versement, mais la confirmation finale doit
            être faite par la caisse centrale ou un rôle central autorisé.
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 1.1,
            mb: 1.6,
          }}
        >
          <MetricCard
            label="Versements"
            value={summary.count}
            caption="Dépôts affichés"
            accent="#2563eb"
            background="rgba(37, 99, 235, 0.09)"
            icon={<AccountBalanceRoundedIcon fontSize="small" />}
          />

          <MetricCard
            label="Confirmés"
            value={formatUsd(summary.confirmedUsd)}
            secondValue={
              summary.confirmedCdf > 0
                ? formatCdf(summary.confirmedCdf)
                : ""
            }
            caption="Confirmé caisse centrale"
            accent="#16a34a"
            background="rgba(22, 163, 74, 0.09)"
            icon={<CheckCircleRoundedIcon fontSize="small" />}
          />

          <MetricCard
            label="En attente"
            value={formatUsd(summary.pendingUsd)}
            secondValue={
              summary.pendingCdf > 0
                ? formatCdf(summary.pendingCdf)
                : ""
            }
            caption="À confirmer par la caisse"
            accent="#d97706"
            background="rgba(217, 119, 6, 0.09)"
            icon={<ScheduleRoundedIcon fontSize="small" />}
          />

          <MetricCard
            label="Annulés"
            value={formatUsd(summary.cancelledUsd)}
            secondValue={
              summary.cancelledCdf > 0
                ? formatCdf(summary.cancelledCdf)
                : ""
            }
            caption="Versements annulés"
            accent="#dc2626"
            background="rgba(220, 38, 38, 0.09)"
            icon={<CancelRoundedIcon fontSize="small" />}
          />
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1.15, sm: 1.25 },
            mb: 1.6,
            borderRadius: 3,
            borderColor: "rgba(148, 163, 184, 0.22)",
            boxShadow: "0 5px 16px rgba(15, 23, 42, 0.025)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "minmax(0, 1fr) 210px 230px 125px 140px",
              },
              gap: 1,
              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Recherche"
              placeholder="Code, shop, organisation, remettant, référence..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />

            <TextField
              select
              size="small"
              label="Méthode"
              value={methodFilter}
              onChange={(event) => setMethodFilter(event.target.value)}
              fullWidth
            >
              <MenuItem value="">Toutes</MenuItem>
              {depositMethods.map((method) => (
                <MenuItem key={method.value} value={method.value}>
                  {method.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Statut caisse"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              fullWidth
            >
              <MenuItem value="">Tous</MenuItem>
              <MenuItem value="pending">En attente caisse centrale</MenuItem>
              <MenuItem value="confirmed">Confirmé caisse centrale</MenuItem>
              <MenuItem value="cancelled">Annulé</MenuItem>
            </TextField>

            <Button
              variant="contained"
              disableElevation
              onClick={() =>
                loadDeposits({
                  search,
                  status: statusFilter,
                  deposit_method: methodFilter,
                })
              }
              disabled={loading}
              sx={{
                width: "100%",
                minWidth: 0,
                whiteSpace: "nowrap",
              }}
            >
              Appliquer
            </Button>

            <Button
              variant="outlined"
              onClick={async () => {
                setSearch("")
                setMethodFilter("")
                setStatusFilter("")
                setPaginationModel({
                  page: 0,
                  pageSize: ROWS_PER_PAGE,
                })

                await loadDeposits({
                  search: "",
                  status: "",
                  deposit_method: "",
                })
              }}
              disabled={loading}
              sx={{
                width: "100%",
                minWidth: 0,
                whiteSpace: "nowrap",
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3.5,
            overflow: "hidden",
            borderColor: "rgba(148, 163, 184, 0.22)",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              px: { xs: 1.4, sm: 1.8 },
              py: 1.15,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 0.8,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography fontWeight={900} color="#0f172a">
                Registre des dépôts caisse
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Origine, montant remis, dette du shop et statut de confirmation
              </Typography>
            </Box>

            <Chip
              size="small"
              variant="outlined"
              label={`${filteredDeposits.length} dépôt${
                filteredDeposits.length > 1 ? "s" : ""
              }`}
            />
          </Box>

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",
                minWidth: 0,
                overflowX: "auto",
                bgcolor: "#ffffff",
              }}
            >
              <Box sx={{ minWidth: 1120 }}>
                <DataGrid
                  autoHeight
                  rows={filteredDeposits}
                  columns={columns}
                  loading={loading}
                  disableRowSelectionOnClick
                  disableColumnSelector
                  pagination
                  paginationMode="client"
                  paginationModel={paginationModel}
                  onPaginationModelChange={(model) =>
                    setPaginationModel({
                      page: model.page,
                      pageSize: ROWS_PER_PAGE,
                    })
                  }
                  pageSizeOptions={[ROWS_PER_PAGE]}
                  rowHeight={74}
                  columnHeaderHeight={50}
                  getRowClassName={(params) =>
                    getDepositRowClass(params.row.status)
                  }
                  localeText={{
                    noRowsLabel: "Aucun dépôt",
                    noResultsOverlayLabel: "Aucun résultat",
                    MuiTablePagination: {
                      labelRowsPerPage: "",
                      labelDisplayedRows: ({ from, to, count }) =>
                        `${from}–${to} sur ${count}`,
                    },
                  }}
                  sx={{
                    border: 0,
                    width: "100%",
                    color: "#334155",
                    fontSize: "0.83rem",
                    bgcolor: "#ffffff",

                    "& .MuiDataGrid-columnHeaders": {
                      bgcolor: "#f8fafc",
                      borderBottom: "1px solid #dfe7f1",
                    },

                    "& .MuiDataGrid-columnHeader": {
                      bgcolor: "#f8fafc",
                      px: 1.25,
                      borderRight:
                        "1px solid rgba(226, 232, 240, 0.60)",
                    },

                    "& .MuiDataGrid-columnHeaderTitle": {
                      color: "#334155",
                      fontSize: "0.76rem",
                      fontWeight: 900,
                      letterSpacing: "0.025em",
                    },

                    "& .MuiDataGrid-cell": {
                      px: 1.25,
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      overflow: "hidden",
                      borderBottom: "1px solid #edf2f7",
                      borderRight:
                        "1px solid rgba(241, 245, 249, 0.85)",
                    },

                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline: "none",
                      },

                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                      {
                        outline: "none",
                      },

                    "& .deposit-row": {
                      transition:
                        "background-color 0.16s ease, box-shadow 0.16s ease",
                    },

                    "& .deposit-row--confirmed": {
                      boxShadow: "inset 4px 0 0 #22c55e",
                      bgcolor: "rgba(240, 253, 244, 0.30)",
                    },

                    "& .deposit-row--pending": {
                      boxShadow: "inset 4px 0 0 #f59e0b",
                      bgcolor: "rgba(255, 251, 235, 0.34)",
                    },

                    "& .deposit-row--cancelled": {
                      boxShadow: "inset 4px 0 0 #ef4444",
                      bgcolor: "rgba(254, 242, 242, 0.30)",
                    },

                    "& .deposit-row--default": {
                      boxShadow: "inset 4px 0 0 #cbd5e1",
                    },

                    "& .deposit-row:hover": {
                      bgcolor: "#f8fbff !important",
                      boxShadow:
                        "inset 4px 0 0 #2563eb, 0 4px 14px rgba(15, 23, 42, 0.045)",
                    },

                    "& .MuiDataGrid-footerContainer": {
                      minHeight: 52,
                      bgcolor: "#fbfdff",
                      borderTop: "1px solid #dfe7f1",
                    },

                    "& .MuiTablePagination-toolbar": {
                      minHeight: 52,
                      px: 1.25,
                    },

                    "& .MuiTablePagination-selectLabel": {
                      display: "none",
                    },

                    "& .MuiTablePagination-input": {
                      display: "none",
                    },
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                p: { xs: 1.1, sm: 1.25 },
                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    Chargement des dépôts...
                  </Typography>
                </Box>
              ) : paginatedMobileDeposits.length === 0 ? (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    Aucun dépôt trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paginatedMobileDeposits.map((deposit) => (
                    <MobileDepositCard
                      key={deposit.id}
                      deposit={deposit}
                      canManageDeposits={canManageDeposits}
                      canDeleteDeposits={canDeleteDeposits}
                      canConfirmCentralDeposit={canConfirmCentralDeposit}
                      onEdit={openEditDialog}
                      onDelete={handleDelete}
                    />
                  ))}
                </Stack>
              )}

              <TablePagination
                component="div"
                count={filteredDeposits.length}
                page={paginationModel.page}
                onPageChange={(_event, newPage) =>
                  setPaginationModel((previous) => ({
                    ...previous,
                    page: newPage,
                    pageSize: ROWS_PER_PAGE,
                  }))
                }
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                labelRowsPerPage=""
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} sur ${count}`
                }
                sx={{
                  mt: 1,
                  bgcolor: "#ffffff",
                  borderRadius: 2.5,

                  "& .MuiTablePagination-selectLabel": {
                    display: "none",
                  },

                  "& .MuiTablePagination-input": {
                    display: "none",
                  },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: "100dvh", sm: "94dvh" },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1.3 }}>
          <Typography fontWeight={900} fontSize={20} color="#0f172a">
            {selectedDeposit
              ? "Modifier / confirmer le dépôt caisse centrale"
              : "Déclarer un versement à la caisse centrale"}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.3 }}
          >
            Enregistrez l’origine, le montant réellement remis, la devise et le
            statut de confirmation du versement.
          </Typography>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            px: { xs: 2, sm: 3 },
          }}
        >
          <Stack spacing={2} mt={1}>
            <Alert severity="success" variant="outlined" sx={{ borderRadius: 2 }}>
              Choisissez le shop d’origine, la devise, le montant réellement
              remis, la personne qui remet l’argent et le mode de versement.
              La caisse centrale confirme ensuite le dépôt.
            </Alert>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              {isStoreLocked ? (
                <TextField
                  label="Shop / origine"
                  value={selectedStoreLabel}
                  fullWidth
                  disabled
                  helperText="Shop verrouillé selon votre compte."
                />
              ) : (
                <TextField
                  select
                  label="Shop / origine de l'argent"
                  name="store_id"
                  value={formData.store_id}
                  onChange={handleChange}
                  fullWidth
                  required
                >
                  {stores.map((store) => (
                    <MenuItem key={store.id} value={store.id}>
                      {store.code} - {store.name}
                      {store.organization_name ? ` • ${store.organization_name}` : ""}
                      {store.expected_to_deposit !== undefined
                        ? ` • reste caisse : ${formatUsd(store.expected_to_deposit)}`
                        : ""}
                      {store.shop_debt_remaining_amount
                        ? ` • dette : ${formatUsd(store.shop_debt_remaining_amount)}`
                        : ""}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                select
                label="Remis par / recouvreur"
                name="user_id"
                value={formData.user_id}
                onChange={handleChange}
                fullWidth
                helperText="Gérant, recouvreur ou responsable qui remet l’argent."
              >
                <MenuItem value="">Non précisé</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.label ||
                      user.full_name ||
                      user.username ||
                      `User ${user.id}`}
                    {user.store_name ? ` • ${user.store_name}` : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {selectedStore && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={900} mb={1}>
                  Situation estimée du shop sélectionné
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(5, 1fr)",
                    },
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Paiements clients au shop
                    </Typography>
                    <Typography fontWeight={800}>{formatUsd(paidSalesAmount)}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Dépenses shop confirmées
                    </Typography>
                    <Typography fontWeight={800}>
                      {formatUsd(confirmedExpensesAmount)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Déjà confirmé caisse centrale
                    </Typography>
                    <Typography fontWeight={800}>
                      {formatUsd(confirmedDepositsAmount)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Reste caisse théorique
                    </Typography>
                    <Typography
                      fontWeight={900}
                      color={expectedToDeposit > 0 ? "warning.main" : "success.main"}
                    >
                      {formatUsd(expectedToDeposit)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Dette restante du shop
                    </Typography>
                    <Typography
                      fontWeight={900}
                      color={shopDebtRemainingAmount > 0 ? "error.main" : "success.main"}
                    >
                      {formatUsd(shopDebtRemainingAmount)}
                    </Typography>
                  </Box>
                </Box>

                <Alert severity="info" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
                  Calcul caisse : paiements clients confirmés moins dépenses
                  confirmées moins dépôts déjà confirmés. La dette affichée est
                  une information séparée pour contrôle.
                </Alert>
              </Paper>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                label="Date du versement"
                name="deposit_date"
                type="datetime-local"
                value={formData.deposit_date}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                select
                label="Devise du montant remis"
                name="deposit_currency"
                value={formData.deposit_currency}
                onChange={handleChange}
                fullWidth
              >
                <MenuItem value="USD">USD - Dollar</MenuItem>
                <MenuItem value="CDF">CDF - Franc congolais</MenuItem>
              </TextField>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                label={
                  formData.deposit_currency === "CDF"
                    ? "Montant remis en CDF"
                    : "Montant remis en USD"
                }
                name="amount"
                type="text"
                value={formData.amount}
                onChange={handleChange}
                fullWidth
                required
                inputProps={{
                  inputMode: "decimal",
                }}
                helperText={
                  formData.deposit_currency === "CDF"
                    ? "Exemples : 31000, 31 000 ou 31 000,50 CDF."
                    : "Exemples : 31, 0,94 ou 31.50 USD."
                }
              />

              <TextField
                label="Taux de change"
                name="exchange_rate"
                type="text"
                value={formData.exchange_rate}
                onChange={handleChange}
                fullWidth
                required={formData.deposit_currency === "CDF"}
                inputProps={{
                  inputMode: "decimal",
                }}
                helperText={
                  formData.deposit_currency === "CDF"
                    ? "Obligatoire pour convertir CDF vers USD. Exemple : 2850 ou 2850,50."
                    : "Optionnel. Utile pour afficher l’équivalent CDF. Exemple : 2850."
                }
              />
            </Box>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={900} mb={1}>
                Conversion automatique du dépôt
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Équivalent USD
                  </Typography>
                  <Typography fontWeight={900}>{formatUsd(formMoney.amount_usd)}</Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Équivalent CDF
                  </Typography>
                  <Typography fontWeight={900}>
                    {formMoney.amount_cdf > 0 ? formatCdf(formMoney.amount_cdf) : "-"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Position caisse après ce dépôt
                  </Typography>
                  <Typography fontWeight={900}>
                    {formatUsd(expectedToDeposit - formMoney.amount_usd)}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                select
                label="Méthode"
                name="deposit_method"
                value={formData.deposit_method}
                onChange={handleChange}
                fullWidth
              >
                {depositMethods.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Statut caisse centrale"
                name="status"
                value={formData.status}
                onChange={handleChange}
                fullWidth
                disabled={!canConfirmCentralDeposit}
                helperText={
                  canConfirmCentralDeposit
                    ? "La caisse centrale peut confirmer ou annuler le dépôt."
                    : "Votre rôle déclare le versement. La confirmation finale se fait à la caisse centrale."
                }
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <TextField
              label="Référence banque / mobile money"
              name="bank_reference"
              value={formData.bank_reference}
              onChange={handleChange}
              fullWidth
              placeholder="Exemple : référence M-Pesa, Airtel Money, banque..."
            />

            <TextField
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={3}
              placeholder="Exemple : argent collecté par le recouvreur, remis à la caisse centrale, écart expliqué..."
            />
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            flexDirection: { xs: "column-reverse", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button onClick={handleClose} disabled={saving} fullWidth={isSmDown}>
            Annuler
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            fullWidth={isSmDown}
            sx={{ fontWeight: 900 }}
          >
            {saving
              ? "Enregistrement..."
              : selectedDeposit
              ? canConfirmCentralDeposit
                ? "Enregistrer / confirmer"
                : "Modifier déclaration"
              : canConfirmCentralDeposit
              ? "Confirmer dépôt"
              : "Soumettre versement"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}

export default DepositsPage